import { useState } from "react";
import { userApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { TID } from "@/lib/constants";
import SignInPrompt from "@/components/SignInPrompt";
import { toast } from "sonner";

const BUCKETS = [
  { key: "keep", title: "🔥 Keep This", desc: "Something is genuinely helping. Say what and why.", testId: TID.zhBucketKeep },
  { key: "trash", title: "🗑️ Trash This", desc: "Something is noise or actively hurting focus.", testId: TID.zhBucketTrash },
  { key: "build", title: "💡 Build This", desc: "An honest request — not a feature demand.", testId: TID.zhBucketBuild },
];

export default function ZeroHour() {
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");
  const [signInOpen, setSignInOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) {
      setSignInOpen(true);
      return;
    }
    if (!selected) {
      toast.error("Pick a bucket first.");
      return;
    }
    setSubmitting(true);
    try {
      await userApi.submitFeedback({ bucket: selected, text: text.trim() || null });
      toast.success("Received. Every note lands with the founder.");
      setSelected(null);
      setText("");
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <div className="mx-auto w-full max-w-[760px] px-4 sm:px-6 pt-5" style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}>
        <h1 className="text-lg font-semibold text-[#F8FAFC]">Zero Hour</h1>
        <p className="mt-1 text-sm text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
          Built by an aspirant, for aspirants. Tell me what’s helping or what needs to be trashed. Every note lands in one inbox.
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {BUCKETS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setSelected(b.key)}
              data-testid={b.testId}
              className={`rounded-2xl border p-4 text-left transition-colors ${selected === b.key ? "border-[#E85A2A] ring-2 ring-[#E85A2A]/15 bg-[#1A2232]" : "border-[#26334D] bg-[#1A2232] hover:bg-[#232E45]"}`}
              aria-pressed={selected === b.key}
            >
              <div className="text-sm font-semibold text-[#F8FAFC]">{b.title}</div>
              <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>{b.desc}</p>
            </button>
          ))}
        </div>

        <textarea
          data-testid={TID.zhFeedbackText}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={800}
          placeholder="Optional: type the sharpest one-line version of your feedback."
          className="mt-4 min-h-[120px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
          style={{ lineHeight: 1.6 }}
        />
        <div className="mt-1 text-right text-xs text-[#94A3B8]">{text.length}/800</div>

        <button
          data-testid={TID.zhSubmit}
          type="button"
          onClick={submit}
          disabled={submitting}
          className="mt-3 w-full rounded-xl bg-[#E85A2A] text-[#F8FAFC] py-3 text-sm font-semibold hover:bg-[#C94A1D] transition-colors disabled:opacity-50"
        >{submitting ? "Sending…" : "Send feedback"}</button>
      </div>

      <SignInPrompt open={signInOpen} onClose={() => setSignInOpen(false)} action="send feedback" context={{ redirect: "/zero-hour" }} />
    </div>
  );
}
